import { Close, KeyboardArrowDown, Search } from '@mui/icons-material';
import {
  Checkbox,
  CircularProgress,
  InputAdornment,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
} from '@mui/material';
import cn from 'classnames';
import { useState } from 'react';

import { useServerSearch } from '@/tracker/hooks/useServerSearch';

import { searchNothing } from './searchNothing';

import s from './FilterChip.module.scss';

type Props<T> = {
  /** Поле фильтра: «Статус», «Исполнитель» */
  label: string;
  value: T[];
  onChange: (value: T[]) => void;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  /** Подпись справа от варианта: доска спринта */
  getHint?: (item: T) => string;
  /** Варианты списком — ищем по ним на месте */
  options?: T[];
  /** Или поиск на сервере — когда вариантов сотни */
  search?: (query: string) => Promise<T[]>;
};

/**
 * Фильтр-плашка, как в фильтрах Яндекс Трекера: «Статус: В работе и ещё 2».
 * По клику — список с поиском и галочками, выбрать можно несколько значений (условие «ИЛИ»)
 */
const FilterChip = <T,>({
  label,
  value,
  onChange,
  getId,
  getLabel,
  getHint,
  options,
  search,
}: Props<T>) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [localQuery, setLocalQuery] = useState('');
  const serverSearch = useServerSearch({
    search: search ?? searchNothing,
    enabled: !!anchor && !!search,
  });

  const query = search ? serverSearch.query : localQuery;
  const setQuery = search ? serverSearch.setQuery : setLocalQuery;
  const needle = localQuery.trim().toLowerCase();
  const found = search
    ? serverSearch.options
    : (options ?? []).filter((item) => getLabel(item).toLowerCase().includes(needle));
  const selectedIds = new Set(value.map(getId));
  // Выбранные — сверху, даже если поиск их не вернул: так их можно снять
  const items = [...value, ...found.filter((item) => !selectedIds.has(getId(item)))];
  const isActive = value.length > 0;
  const isSearching = !!search && serverSearch.isLoading;

  const toggle = (item: T) => {
    const id = getId(item);

    onChange(
      selectedIds.has(id) ? value.filter((selected) => getId(selected) !== id) : [...value, item],
    );
  };

  const close = () => {
    setAnchor(null);
    setQuery('');
  };

  const summary = value.length > 1 ? `${getLabel(value[0])} и ещё ${value.length - 1}` : '';

  return (
    <span className={cn(s.chip, isActive && s.chip_active)}>
      <button
        type="button"
        className={s.chip__button}
        aria-haspopup="listbox"
        onClick={(event) => setAnchor(event.currentTarget.parentElement)}
      >
        {label}
        {isActive && <span className={s.chip__value}>: {summary || getLabel(value[0])}</span>}
        {!isActive && <KeyboardArrowDown fontSize="small" className={s.chip__arrow} />}
      </button>
      {isActive && (
        <button
          type="button"
          className={s.chip__clear}
          aria-label={`Сбросить фильтр «${label}»`}
          onClick={() => onChange([])}
        >
          <Close fontSize="inherit" />
        </button>
      )}

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { className: s.popover } }}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Поиск"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': `Поиск: ${label}` },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <div className={s.popover__list} role="listbox" aria-multiselectable aria-label={label}>
          {isSearching && (
            <div className={s.popover__state}>
              <CircularProgress size={20} />
            </div>
          )}
          {!isSearching && !items.length && (
            <div className={s.popover__state}>Ничего не нашлось</div>
          )}
          {items.map((item) => (
            <MenuItem
              key={getId(item)}
              dense
              role="option"
              aria-selected={selectedIds.has(getId(item))}
              onClick={() => toggle(item)}
            >
              {/* Только картинка: выбор объявляет сам вариант через aria-selected */}
              <Checkbox
                size="small"
                checked={selectedIds.has(getId(item))}
                tabIndex={-1}
                slotProps={{ input: { 'aria-hidden': true } }}
              />
              <ListItemText primary={getLabel(item)} />
              {getHint && <span className={s.popover__hint}>{getHint(item)}</span>}
            </MenuItem>
          ))}
        </div>
      </Popover>
    </span>
  );
};

export { FilterChip };
